import React, { createContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { theme } from 'theme';
import translations from 'utils/translations';
import { useDataHelper } from 'utils/dataHelper';
import { useTheme, styled } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';

import { SdmTextButton, SdmTypography, SdmSpacer } from 'components';
import { useNetwork } from 'services/network';

const FooterCart = styled('div')(({ cartTotal, isSmDown }) => ({
  transition: 'all 0.8s',
  transform: `translateY(${cartTotal ? 0 : 110}%)`,
  position: 'fixed',
  bottom: 0,
  right: 0,
  left: 0,
  zIndex: 110,
  minHeight: isSmDown ? 174 : 100,
  backgroundColor: theme.colors.primary.white,
  padding: isSmDown ? 5 : 0,
  webkitBoxShadow: '0px -10px 16px 0px rgba(0,0,0,0.1)',
  boxShadow: '0px -10px 16px 0px rgba(0,0,0,0.1)',
}));

const Container = styled('div')(({ cartTotal, isSmDown }) => ({
  display: 'flex',
  flexDirection: isSmDown ? 'column' : 'row',
  alignItems: isSmDown ? 'flex-start' : 'center',
  justifyContent: isSmDown ? 'center' : 'flex-end',
  transition: 'all 0.8s',
  transform: `translateY(${cartTotal ? 0 : 110}%)`,
  overflowX: isSmDown ? 'scroll' : 'hidden',
  minHeight: isSmDown ? 174 : 100,
  width: isSmDown ? '90%' : '80%',
  margin: '0 auto',
  maxWidth: 830,
  padding: isSmDown ? 5 : 0,
}));

const SubFooter = styled('div')(() => ({
  height: 140,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  // overflowX: 'scroll',
  paddingLeft: 8,
}));

const Card = styled('div')(() => ({
  backgroundColor: '#f3f3f3',
  display: 'flex',
  flexDirection: 'column',
  marginRight: 10,
  height: 116,
  width: 272,
  padding: 16,
  marginBottom: 8,
}));
//272 116

const CartContext = createContext();

const CartContextProvider = ({ children, loggedUser }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Tickets(intl).page;
  const history = useHistory();
  const mediaTheme = useTheme();
  const network = useNetwork();
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  const isXsDown = useMediaQuery(mediaTheme.breakpoints.down('xs'));
  const isIphone5 = useMediaQuery(mediaTheme.breakpoints.down(376));
  //State
  const [cartTotal, setCartTotal] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const [nextStep, setNextStep] = useState('');
  const [merchandising, setMerchandising] = useState([]);
  const [globalFilledTickets, setGlobalFilledTickets] = useState([]);
  const [productsDataForLayer, setProductsDataForLayer] = useState([]);
  const [freezedCart, setFreezedCart] = useState([]);
  const [isProceedDisabled, setIsProceedDisabled] = useState(false);
  const [hideCartFooter, setHideCartFooter] = useState(false);
  const [orderNumber, setOrderNumber] = useState();
  const [paymentIntentId, setPaymentIntentId] = useState();
  const [customer, setCustomer] = useState();
  const [emailProperties, setEmailProperties] = useState({
    send_customer_email: false,
    send_owners_email: false,
  });
  const [showSubFooter, setShowSubFooter] = useState(false);

  const dataHelper = useDataHelper();

  // const DummyAzureUser = {
  //   accountid: '001MA000004Xc7SYAS',
  //   brand: null,
  //   campiDeprecati:
  //     'Nazionalita__c,MailingCity,NomeAgenzia__c,CitySedeAgenzia__c,WebSite__c,CasaEditriceTestata__c,TipologiaTestataAgenzia__c,SettoreTestataAgenzia__c',
  //   firstname: 'M',
  //   flagMarketing: true,
  //   flagProfilazione: true,
  //   lastname: 'Z',
  //   lingua: 'IT',
  //   nazionalita: null,
  //   personBirthdate: null,
  //   personEmail: '12.mz.jkl@gmail.com',
  //   personMailingCity: null,
  //   personMailingCountry: 'ITA',
  //   personMailingPostalCode: null,
  //   personMailingState: null,
  //   personMailingStreet: null,
  //   ragioneSociale: null,
  //   role: 'Titolare_Managing_director',
  //   tipologia: 'A&D Professionals',
  //   sottoTipologia: "Decoratore d'interni",
  // };

  useEffect(() => {
    const logArray = cartItems
      .concat(freezedCart)
      .map((p) => p.quantity * p.price)
      .reduce((prev, curr) => prev + curr, 0);
    setCartTotal(logArray);
  }, [cartItems]);

  useEffect(() => {
    const fetchCustomer = async () => {
      const { customerData } = await network.salesforceCustomerLambda({
        federationId: loggedUser?.salId,
      });
      if (customerData?.accountid) setCustomer(customerData);
      // setCustomer(DummyAzureUser);
    };
    if (loggedUser?.salId && !customer) fetchCustomer();
  }, [loggedUser]);

  const handleRemove = (productId) => {
    const slicedCart = cartItems.filter((p) => p.pId !== productId);
    setCartItems(slicedCart);
  };

  return (
    <CartContext.Provider
      value={{
        nextStep,
        setNextStep,
        cartItems,
        setCartItems,
        globalFilledTickets,
        setGlobalFilledTickets,
        cartTotal,
        setCartTotal,
        setIsProceedDisabled,
        setHideCartFooter,
        freezedCart,
        setFreezedCart,
        orderNumber,
        setOrderNumber,
        paymentIntentId,
        setPaymentIntentId,
        customer,
        setCustomer,
        emailProperties,
        setEmailProperties,
        merchandising,
        setMerchandising,
        productsDataForLayer,
        setProductsDataForLayer,
      }}
    >
      {children}
      {!hideCartFooter && (
        <FooterCart
          cartTotal={cartTotal}
          isSmDown={isSmDown}
          isXsDown={isXsDown}
        >
          {cartTotal ? (
            <Container cartTotal={cartTotal} isSmDown={isSmDown}>
              {!isSmDown ? (
                <SdmTypography
                  variant={'twentyFourFont'}
                  style={{ marginRight: isSmDown ? 0 : 50 }}
                >
                  {i18n.selectedTicketsCard.subtotal}:{' '}
                  {dataHelper.currencyFormatter.format(cartTotal)}
                </SdmTypography>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    width: '100%',
                  }}
                >
                  <SdmTypography variant={'h2'}>
                    {i18n.selectedTicketsCard.title}
                  </SdmTypography>
                  <SdmTypography variant={'h3'}>
                    {i18n.selectedTicketsCard.subtotal}:{' '}
                    {dataHelper.currencyFormatter.format(cartTotal)}
                  </SdmTypography>
                  <SdmSpacer height={8} />
                </div>
              )}
              {showSubFooter &&
                isSmDown &&
                cartItems.filter((p) => p.quantity > 0 && !p.isMerchandising)
                  .length > 0 && (
                  <>
                    <SubFooter showSubFooter={showSubFooter}>
                      {cartItems.map(
                        (p) =>
                          p.quantity > 0 &&
                          !p.isMerchandising && (
                            <Card key={p.pId}>
                              <div style={{ flexGrow: '1' }}>
                                <SdmTypography margin={0} variant={'text'}>
                                  {p.name}
                                </SdmTypography>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  paddingBottom: 8,
                                }}
                              >
                                <SdmTypography margin={0} variant={'text'}>
                                  {dataHelper.currencyFormatter.format(p.price)}
                                </SdmTypography>
                                <SdmTypography margin={0} variant={'text'}>
                                  {i18n.selectedTicketsCard.quantity}:{' '}
                                  {p.quantity}
                                </SdmTypography>
                              </div>
                              <SdmTypography
                                margin={0}
                                variant={'text'}
                                style={{
                                  cursor: 'pointer',
                                  color: theme.colors.primary.red,
                                  width: 0,
                                }}
                                onClick={() => handleRemove(p.pId)}
                              >
                                {i18n.selectedTicketsCard.remove}
                              </SdmTypography>
                            </Card>
                          )
                      )}
                    </SubFooter>
                  </>
                )}

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {isSmDown && (
                  <SdmTextButton
                    variant={'red'}
                    text={
                      i18n.selectedTicketsCard[!showSubFooter ? 'view' : 'hide']
                    }
                    textVariant={'h5Regular'}
                    onClick={() => setShowSubFooter((prev) => !prev)}
                    disabled={cartItems.length === 0}
                    style={{
                      width: isIphone5 ? 131 : isSmDown ? 162 : 158,
                      height: isSmDown ? 40 : 50,
                      border: `1px solid ${theme.colors.primary.red}`,
                    }}
                  />
                )}

                <SdmTextButton
                  variant={'redFill'}
                  text={i18n.selectedTicketsCard.proceed}
                  textVariant={'h4Regular'}
                  onClick={() => {
                    window.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: 'smooth',
                    });
                    history.push(nextStep || '/');
                  }}
                  disabled={isProceedDisabled}
                  style={{
                    width: 125,
                    height: isSmDown ? 40 : 50,
                    marginRight: 0,
                  }}
                />
              </div>
            </Container>
          ) : null}
        </FooterCart>
      )}
    </CartContext.Provider>
  );
};

export { CartContextProvider, CartContext };
